import { PlusIcon } from '@shopify/polaris-icons';
import { Form, TextField } from '../components/forms';
/**
 * REGISTER PAGE
 *
 * Create a new account with email and password.
 * Also supports Google OAuth registration.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Box,
  Button,
  Divider,
} from '@shopify/polaris';
import { useAuth } from '../App';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

// Password requirements
const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_REQUIREMENTS = [
  { test: (p: string) => p.length >= PASSWORD_MIN_LENGTH, message: `At least ${PASSWORD_MIN_LENGTH} characters` },
  { test: (p: string) => /[A-Z]/.test(p), message: 'One uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p), message: 'One lowercase letter' },
  { test: (p: string) => /[0-9]/.test(p), message: 'One number' },
];

export default function Register() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Get error from URL params
  const searchParams = new URLSearchParams(location.search);
  const urlError = searchParams.get('error');

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Validate password on change
  const getPasswordStrength = useCallback((pwd: string) => {
    const passed = PASSWORD_REQUIREMENTS.filter(req => req.test(pwd));
    return {
      score: passed.length,
      total: PASSWORD_REQUIREMENTS.length,
      requirements: PASSWORD_REQUIREMENTS.map(req => ({
        ...req,
        met: req.test(pwd),
      })),
    };
  }, []);

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword;

  const handleRegister = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});

    // Client-side validation
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = 'Name is required';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email';
    }

    if (passwordStrength.score < PASSWORD_REQUIREMENTS.length) {
      errors.password = 'Password does not meet requirements';
    }

    if (!passwordsMatch) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password }),
      });

      const result = await response.json();

      if (!result.success) {
        if (result.code === 'EMAIL_EXISTS') {
          setFieldErrors({ email: 'This email is already registered' });
        } else if (result.code === 'INVALID_PASSWORD') {
          setFormError(result.error || 'Password does not meet requirements');
        } else {
          setFormError(result.error || 'Registration failed');
        }
        return;
      }

      // Success - redirect to login with success message
      navigate('/login?success=registered');
    } catch {
      setFormError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [name, email, password, passwordStrength, passwordsMatch, navigate]);

  const handleGoogleSignIn = () => {
    window.location.href = `${API_URL}/api/auth/google`;
  };

  const getErrorMessage = (errorCode: string | null): string | null => {
    switch (errorCode) {
      case 'access_denied':
        return 'You cancelled the sign-in process. Please try again.';
      case 'email_conflict':
        return 'This email is already associated with another account.';
      default:
        return errorCode ? `Registration error: ${errorCode}` : null;
    }
  };

  const errorMessage = urlError ? getErrorMessage(urlError) : formError;

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f6f6f7'
      }}>
        <Text as="p" variant="bodyMd" tone="subdued">Loading...</Text>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f6f6f7',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Logo/Brand */}
          <Box paddingBlockEnd="400">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                Whenly
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Delivery Date & Warehouse Management
              </Text>
            </BlockStack>
          </Box>

          {/* Error Banner */}
          {errorMessage && (
            <Banner tone="critical" onDismiss={() => {
              setFormError(null);
              if (urlError) navigate('/register');
            }}>
              {errorMessage}
            </Banner>
          )}

          {/* Register Card */}
          <Card>
            <BlockStack gap="500">
              <Text as="h2" variant="headingMd">
                Create your account
              </Text>

              {/* Registration Form */}
              <Form onSubmit={handleRegister}>
                <BlockStack gap="400">
                  <TextField
                    label="Full name"
                    type="text"
                    requiredIndicator value={name}
                    onChange={setName}
                    autoComplete="name"
                    disabled={isSubmitting}
                    error={fieldErrors.name}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    requiredIndicator value={email}
                    onChange={setEmail}
                    autoComplete="email"
                    disabled={isSubmitting}
                    error={fieldErrors.email}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    requiredIndicator value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    error={fieldErrors.password}
                    helpText={
                      password.length > 0 ? (
                        <BlockStack gap="100">
                          {passwordStrength.requirements.map((req, i) => (
                            <Text
                              key={i}
                              as="span"
                              variant="bodySm"
                              tone={req.met ? 'success' : 'subdued'}
                            >
                              {req.met ? '✓' : '○'} {req.message}
                            </Text>
                          ))}
                        </BlockStack>
                      ) : undefined
                    }
                  />
                  <TextField
                    label="Confirm password"
                    type="password"
                    requiredIndicator value={confirmPassword}
                    onChange={setConfirmPassword}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    error={fieldErrors.confirmPassword}
                  />
                  <Button icon={PlusIcon}
                    submit
                    variant="primary"
                    size="large"
                    fullWidth
                    loading={isSubmitting}
                    disabled={
                      !name ||
                      !email ||
                      passwordStrength.score < PASSWORD_REQUIREMENTS.length ||
                      !passwordsMatch
                    }
                  >
                    Create account
                  </Button>
                </BlockStack>
              </Form>

              {/* Divider */}
              <InlineStack gap="400" align="center" blockAlign="center">
                <div style={{ flex: 1 }}><Divider /></div>
                <Text as="span" variant="bodySm" tone="subdued">or</Text>
                <div style={{ flex: 1 }}><Divider /></div>
              </InlineStack>

              {/* Google Sign Up */}
              <button
                onClick={handleGoogleSignIn}
                type="button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 500,
                  backgroundColor: '#fff',
                  border: '1px solid #c4cdd5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, border-color 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f6f6f7';
                  e.currentTarget.style.borderColor = '#8c9196';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.borderColor = '#c4cdd5';
                }}
              >
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>

              {/* Sign In Link */}
              <Box paddingBlockStart="200">
                <Text as="p" variant="bodyMd" alignment="center">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    style={{
                      color: '#2C6ECB',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Sign in
                  </Link>
                </Text>
              </Box>

              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                By creating an account, you agree to our Terms of Service and Privacy Policy.
              </Text>
            </BlockStack>
          </Card>

          {/* Help Text */}
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Need help? Contact{' '}
            <a href="mailto:support@whenly.app" style={{ color: '#2C6ECB' }}>
              support@whenly.app
            </a>
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}

// Google Icon Component
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
